import { createContext, useContext, useState, ReactNode, useEffect, useRef } from 'react';
import { usePerformRequest } from '../lib/usePerformRequest';
import { NonPaginatedResponse } from '../types';
import { IUserProps } from '../types/User';
import { Endpoints } from '../lib/endpoints';

export interface AuthContextType {
  user: IUserProps | null;
  loading: boolean;
  login: (emailOrPhone: string, password: string) => Promise<void>;
  register: (email: string, phone: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  reloadUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const STORAGE_TOKEN_KEY = 't';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [hasToken, setHasToken] = useState(() => !!localStorage.getItem(STORAGE_TOKEN_KEY));
  const [loadTimeout, setLoadTimeout] = useState(false);
  
  // Update route based on token state - this triggers usePerformRequest to fetch when route changes from '' to actual route
  const profileRoute = hasToken ? Endpoints.GetProfile().route : '';

  const { 
    data: profileResponse, 
    isLoading: isLoadingUser, 
    reloadData: reloadUserFunc,
    status: profileStatus,
  } = usePerformRequest<NonPaginatedResponse<IUserProps>>({
    method: Endpoints.GetProfile().method,
    route: profileRoute,
  });

  // Set timeout to prevent infinite loading (10 seconds max)
  useEffect(() => {
    if (hasToken && !profileResponse?.data) {
      const timeout = setTimeout(() => {
        setLoadTimeout(true);
        // If timeout reached and no user, clear token to prevent infinite loading
        if (!profileResponse?.data) {
          localStorage.removeItem(STORAGE_TOKEN_KEY);
          setHasToken(false);
        }
      }, 10000); // 10 seconds
      return () => clearTimeout(timeout);
    } else {
      setLoadTimeout(false);
    }
  }, [hasToken, profileResponse]);

  // Extract user from API response - profileResponse is NonPaginatedResponse<IUserProps>
  // so profileResponse.data contains the IUserProps
  const user = profileResponse?.data || null;
  
  // Use ref to track user for polling in reloadUser
  const userRef = useRef<IUserProps | null>(null);
  userRef.current = user;

  // Sync token state with localStorage
  useEffect(() => {
    const checkToken = () => {
      const currentToken = localStorage.getItem(STORAGE_TOKEN_KEY);
      const tokenExists = !!currentToken;
      // Only update state if it actually changed to prevent unnecessary re-renders
      setHasToken(prev => prev !== tokenExists ? tokenExists : prev);
    };
    
    // Check on mount
    checkToken();
    
    // Listen for storage changes (when token is set/removed in other tabs/components)
    window.addEventListener('storage', checkToken);
    
    // Check token periodically (for same-tab updates) - reduced frequency to prevent excessive checks
    const interval = setInterval(checkToken, 500); // Check every 500ms for faster response
    
    return () => {
      window.removeEventListener('storage', checkToken);
      clearInterval(interval);
    };
  }, []);

  // Handle auth errors - clear token if unauthorized
  useEffect(() => {
    if (profileStatus === 401 || profileStatus === 403) {
      localStorage.removeItem(STORAGE_TOKEN_KEY);
      setHasToken(false);
      // Clear user data on auth error
      // This will be handled by the route becoming empty string
    }
  }, [profileStatus]);

  // Loading state: loading when:
  // 1. We're actively fetching user data (isLoadingUser) AND we have a token
  // 2. We have a token but no user yet AND we haven't gotten an error response yet AND timeout hasn't been reached
  // Stop loading if we get a 401/403 (unauthorized) or timeout reached - means token is invalid or request failed
  const loading = (isLoadingUser && hasToken && !loadTimeout) || (hasToken && !user && profileStatus !== 401 && profileStatus !== 403 && profileStatus !== 'undefined' && !loadTimeout);

  const login = async (emailOrPhone: string, password: string): Promise<void> => {
    // Login is handled in Login component - just a placeholder
    return Promise.resolve();
  };

  const register = async (email: string, phone: string, password: string): Promise<void> => {
    // Registration is handled in Register component - just a placeholder
    return Promise.resolve();
  };

  const logout = async (): Promise<void> => {
    localStorage.removeItem(STORAGE_TOKEN_KEY);
    setHasToken(false);
  };

  const handleReloadUser = async (): Promise<void> => {
    const currentToken = localStorage.getItem(STORAGE_TOKEN_KEY);
    if (!currentToken) {
      setHasToken(false);
      return;
    }
    
    // Force update hasToken state immediately to trigger profile fetch
    setHasToken(true);
    
    // Wait for React to process the state update and route to be updated
    // This ensures usePerformRequest sees the route change
    await new Promise(resolve => setTimeout(resolve, 150));
    
    // Explicitly reload to ensure fresh data - this will fetch the profile
    await reloadUserFunc();
    
    // Poll until user is actually loaded (state update might be async)
    let attempts = 0;
    const maxAttempts = 100; // 10 seconds max wait (increased from 5)
    while (attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 100));
      // Check ref to get latest user value
      if (userRef.current) {
        // Wait a bit more to ensure React state is fully updated
        await new Promise(resolve => setTimeout(resolve, 200));
        return; // User loaded successfully
      }
      // Check if token was cleared (auth error)
      const tokenStillExists = !!localStorage.getItem(STORAGE_TOKEN_KEY);
      if (!tokenStillExists) {
        setHasToken(false);
        throw new Error("Authentication failed");
      }
      attempts++;
    }
    
    // If we get here, user didn't load - check if it's an auth error
    const tokenStillExists = !!localStorage.getItem(STORAGE_TOKEN_KEY);
    if (!tokenStillExists || profileStatus === 401 || profileStatus === 403) {
      setHasToken(false);
      throw new Error("Failed to load user profile");
    }
    
    // If we still have a token but no user, wait a bit more
    // Sometimes the profile response takes longer
    if (tokenStillExists && !userRef.current) {
      await new Promise(resolve => setTimeout(resolve, 500));
      if (userRef.current) {
        return; // User finally loaded
      }
    }
  };

  
  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout, 
      reloadUser: handleReloadUser 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
